// The editable carousel deck's slide FILMSTRIP: navigation + management (select / add /
// duplicate / delete / reorder). Renders as a composer-level bottom dock (SlideFilmstripDock,
// mounted in ComposerShell next to ReelTimelineDock — same layout as the video timeline), NOT
// inside the platform preview card; the ACTIVE slide renders there as the live canvas
// (CanvasMediaWithBar). Reuses CompositionCanvas for live slide thumbnails. See
// CompositionEditorProvider's CarouselProviderInner + carousel-ops.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Copy, Plus, Sparkles, Trash2 } from 'lucide-react'
import { cn } from 'lib/utils'
import { CompositionCanvas } from '../CompositionCanvas'
import type { PostComposition, SlideRole } from '../types'
import { useCarouselDeck } from './CompositionEditorProvider'

const THUMB_H = 112

const ROLE_LABEL: Record<SlideRole, string> = {
  hook: 'Hook',
  rehook: 'Hook 2',
  body: 'Body',
  cta: 'CTA',
}

/** The composer-level bottom dock for carousels (desktop): the slide filmstrip, docked in the
 *  same slot as the reel timeline. Renders nothing for non-carousel posts. */
export function SlideFilmstripDock({
  onAskAISlide,
}: {
  /** Pin a per-slide chat context ("Slide N of M") so an AI revise targets THIS slide. */
  onAskAISlide?: (index: number, slideCount: number) => void
}) {
  const deck = useCarouselDeck()
  if (!deck) return null
  return (
    <div className="flex shrink-0 items-center gap-2 overflow-x-auto border-t border-line bg-bg-2 px-3 py-2">
      {deck.slides.map((slide, i) => (
        <SlideThumb
          key={slide.slide?.id ?? i}
          slide={slide}
          index={i}
          onAskAISlide={onAskAISlide}
        />
      ))}
      {deck.canAdd && (
        <button
          type="button"
          onClick={deck.addSlide}
          title="Add slide"
          aria-label="Add slide"
          className="flex shrink-0 flex-col items-center justify-center gap-1 rounded-[10px] border border-dashed border-line-bright text-paper-dim transition-colors hover:border-paper-faint hover:bg-bg-3 hover:text-paper"
          style={{ height: THUMB_H, width: THUMB_H * 0.8 }}
        >
          <Plus size={18} />
          <span className="text-[10px] font-medium">Slide</span>
        </button>
      )}
    </div>
  )
}

function SlideThumb({
  slide,
  index,
  onAskAISlide,
}: {
  slide: PostComposition
  index: number
  onAskAISlide?: (index: number, slideCount: number) => void
}) {
  const deck = useCarouselDeck()
  // Two-step delete: the first tap arms ("tap again"), the second (within 3s) removes — so a
  // stray tap never silently drops a slide.
  const [armed, setArmed] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )
  const onDelete = () => {
    if (!armed) {
      setArmed(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setArmed(false), 3000)
      return
    }
    if (timer.current) clearTimeout(timer.current)
    setArmed(false)
    deck?.deleteSlide(index)
  }
  if (!deck) return null
  const active = index === deck.activeIndex
  const role = slide.slide?.role
  const width = THUMB_H * (slide.canvas.width / slide.canvas.height)

  return (
    <div className="group/thumb relative shrink-0">
      <button
        type="button"
        onClick={() => deck.selectSlide(index)}
        aria-label={`Slide ${index + 1}${role ? ` (${ROLE_LABEL[role]})` : ''}`}
        aria-current={active}
        data-slide-index={index}
        className={cn(
          'relative block overflow-hidden rounded-[10px] border transition-all',
          active ? 'border-acid ring-2 ring-acid/40' : 'border-line hover:border-paper-faint'
        )}
        style={{ height: THUMB_H, width }}
      >
        {/* A live, read-only render of the slide (no overlay/toolbars). */}
        <CompositionCanvas composition={slide} maxHeight={THUMB_H} />
        {/* Swipe index + role badge. */}
        <span className="absolute top-1 left-1 rounded bg-black/60 px-1 text-[9px] leading-tight font-semibold text-paper">
          {index + 1}
        </span>
        {role && (
          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[8px] leading-tight font-medium tracking-wide text-paper-dim uppercase">
            {ROLE_LABEL[role]}
          </span>
        )}
      </button>

      {/* Hover controls: move / duplicate / delete. Hidden at rest so the strip stays calm. */}
      <div className="pointer-events-none absolute -top-2 right-0 flex gap-0.5 opacity-0 transition-opacity group-hover/thumb:pointer-events-auto group-hover/thumb:opacity-100">
        {onAskAISlide && (
          <ThumbBtn
            label={`Ask Taka about slide ${index + 1}`}
            onClick={() => {
              deck.selectSlide(index)
              onAskAISlide(index, deck.slides.length)
            }}
          >
            {/* The canonical Ask-Taka look: stars icon + the words, in every inline menu. */}
            <Sparkles size={10} />
            <span className="text-[9px]">Ask Taka</span>
          </ThumbBtn>
        )}
        {index > 0 && (
          <ThumbBtn label="Move left" onClick={() => deck.moveSlide(index, index - 1)}>
            <ChevronLeft size={11} />
          </ThumbBtn>
        )}
        {index < deck.slides.length - 1 && (
          <ThumbBtn label="Move right" onClick={() => deck.moveSlide(index, index + 1)}>
            <ChevronRight size={11} />
          </ThumbBtn>
        )}
        {deck.canAdd && (
          <ThumbBtn label="Duplicate slide" onClick={() => deck.duplicateSlide(index)}>
            <Copy size={10} />
          </ThumbBtn>
        )}
        {deck.canDelete && (
          <ThumbBtn
            label={armed ? 'Tap again to delete' : 'Delete slide'}
            onClick={onDelete}
            danger={armed}
          >
            <Trash2 size={10} />
          </ThumbBtn>
        )}
      </div>
    </div>
  )
}

function ThumbBtn({
  label,
  onClick,
  danger,
  children,
}: {
  label: string
  onClick: () => void
  danger?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={e => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        // min-w (not fixed w): icon-only buttons stay circular, the Ask-Taka icon+label
        // variant grows into a pill instead of overflowing the 20px circle.
        'flex h-5 min-w-5 items-center justify-center gap-1 rounded-full border px-1 whitespace-nowrap shadow transition-colors',
        danger
          ? 'border-red-400 bg-red-500 text-white'
          : 'border-line bg-bg-3 text-paper-dim hover:bg-bg-4 hover:text-paper'
      )}
    >
      {children}
    </button>
  )
}
